import React, {useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import Grid from '@material-ui/core/Grid';
import {format, isValid} from 'date-fns';
import {useEvent, useStore} from 'effector-react';
import i18next from 'i18next';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import {toast} from 'react-toastify';
import * as yup from 'yup';

import {BackTo} from 'components/common/BackTo';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {Modal} from 'components/ui-kit/Modal';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {fetchDropoutBatchesFx, recallBatchFx} from 'models/registries/gtinsGrid/upload';
import {
    BatchRecallFormData,
    BatchRecallParams,
    DropoutBatchResponse,
    RecallRouteState,
} from 'types/gtins';
import {useDropoutDictionaryOptions} from 'utils/dropoutDictionary';

import {ButtonWrapper, ModalText, Root} from '../RecallApproveModal/styled';
import {
    ContentWrapper,
    FormField,
    FormWrapper,
    HeadlineText,
    NavigationButtonsContainer,
    SubheadlineText,
} from '../styled';

import {BatchSection, BatchSectionTitle, BatchTable, BatchTableWrapper, RadioCell} from './styled';

const schema = yup.object().shape({
    reason: yup.string().required(i18next.t('components.form.required')),
    productCode: yup
        .string()
        .trim()
        .required(i18next.t('components.form.required')),
    batchNumber: yup
        .string()
        .trim()
        .required(i18next.t('components.form.required')),
    comment: yup.string().notRequired(),
});

const formatDate = (value?: string | null) => {
    if (!value) return '-';

    const date = new Date(value);
    return isValid(date) ? format(date, 'dd.MM.yyyy') : '-';
};

export const BatchRecall = () => {
    const {t} = useTranslation();
    const location = useLocation<RecallRouteState>();
    const routeState = location.state;
    const {reasons} = useDropoutDictionaryOptions();
    const searchBatches = useEvent(fetchDropoutBatchesFx);
    const recallBatch = useEvent(recallBatchFx);
    const isSearchPending = useStore(fetchDropoutBatchesFx.pending);
    const isRecallPending = useStore(recallBatchFx.pending);
    const [batches, setBatches] = useState<DropoutBatchResponse[]>([]);
    const [selectedBatch, setSelectedBatch] = useState<DropoutBatchResponse | null>(null);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

    const {control, formState, getValues, handleSubmit, watch} = useForm<BatchRecallFormData>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: {
            reason: '',
            productCode: routeState?.productCode ?? '',
            batchNumber: routeState?.batchNumber ?? '',
            comment: '',
        },
    });

    const productCode = watch('productCode');
    const batchNumber = watch('batchNumber');

    useEffect(() => {
        setBatches([]);
        setSelectedBatch(null);
    }, [productCode, batchNumber]);

    const findBatches = async (data: BatchRecallFormData) => {
        const result = await searchBatches({
            productCode: data.productCode.trim(),
            batchNumber: data.batchNumber.trim(),
        }).catch(() => null);

        if (!result) return null;

        setBatches(result);

        if (result.length === 0) {
            toast.error(t('pages.documents.batchDropout.noBatches'));
        }

        return result;
    };

    const requestRecall = async (data: BatchRecallFormData) => {
        if (selectedBatch) {
            setIsConfirmationOpen(true);
            return;
        }

        const result = await findBatches(data);
        if (result?.length === 1) {
            setSelectedBatch(result[0]);
            setIsConfirmationOpen(true);
        }
    };

    const confirmRecall = async () => {
        if (!selectedBatch) return;

        const data = getValues();
        const payload: BatchRecallParams = {
            reason: data.reason,
            productCode: data.productCode.trim(),
            batchNumber: selectedBatch.batchNumber || data.batchNumber.trim(),
            batchId: selectedBatch.batchId,
            ...(data.comment?.trim() ? {comment: data.comment.trim()} : {}),
        };

        try {
            await recallBatch(payload);
            setIsConfirmationOpen(false);
        } catch {
            // The effect watcher displays the server error.
        }
    };

    const batchRows = useMemo(
        () =>
            batches.map((batch) => (
                <tr key={batch.batchId} onClick={() => setSelectedBatch(batch)}>
                    <RadioCell>
                        <input
                            type="radio"
                            name="dropoutBatch"
                            checked={selectedBatch?.batchId === batch.batchId}
                            onChange={() => setSelectedBatch(batch)}
                            aria-label={batch.batchNumber}
                        />
                    </RadioCell>
                    <td>{batch.batchNumber || '-'}</td>
                    <td>{formatDate(batch.productionDate)}</td>
                    <td>{formatDate(batch.expirationDate)}</td>
                </tr>
            )),
        [batches, selectedBatch],
    );

    return (
        <>
            <BackTo to={ROUTE.REGISTRIES_GTIN}>{t('pages.documents.back')}</BackTo>
            <ContentWrapper>
                <HeadlineText>{t('pages.documents.batchDropout.title')}</HeadlineText>
                <SubheadlineText>{t('pages.documents.batchDropout.description')}</SubheadlineText>
                <form onSubmit={handleSubmit(requestRecall)}>
                    <FormWrapper>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormField>
                                    <Controller
                                        control={control}
                                        name="reason"
                                        render={({value, onChange}, {invalid}) => (
                                            <Select
                                                label={t('pages.documents.batchDropout.reason')}
                                                isRequired
                                                disabled={isSearchPending || isRecallPending}
                                                value={reasons.find(
                                                    (option) => option.value === value,
                                                )}
                                                options={reasons}
                                                hasError={invalid}
                                                onChange={(option: SelectOption<string> | null) =>
                                                    onChange(option?.value ?? '')
                                                }
                                            />
                                        )}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item xs={12}>
                                <FormField>
                                    <Controller
                                        control={control}
                                        name="productCode"
                                        render={(props, {invalid}) => (
                                            <Input
                                                {...props}
                                                label={t(
                                                    'pages.documents.batchDropout.productCode',
                                                )}
                                                isRequired
                                                disabled={isSearchPending || isRecallPending}
                                                hasError={invalid}
                                            />
                                        )}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item xs={12}>
                                <FormField>
                                    <Controller
                                        control={control}
                                        name="batchNumber"
                                        render={(props, {invalid}) => (
                                            <Input
                                                {...props}
                                                label={t(
                                                    'pages.documents.batchDropout.batchNumber',
                                                )}
                                                isRequired
                                                disabled={isSearchPending || isRecallPending}
                                                hasError={invalid}
                                            />
                                        )}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item xs={12}>
                                <FormField>
                                    <Controller
                                        control={control}
                                        name="comment"
                                        render={(props) => (
                                            <Input
                                                {...props}
                                                label={t('pages.documents.batchDropout.comment')}
                                                disabled={isSearchPending || isRecallPending}
                                            />
                                        )}
                                    />
                                </FormField>
                            </Grid>
                        </Grid>

                        {batches.length > 1 && (
                            <BatchSection>
                                <BatchSectionTitle>
                                    {t('pages.documents.batchDropout.batchDetails')}
                                </BatchSectionTitle>
                                <BatchTableWrapper>
                                    <BatchTable>
                                        <thead>
                                            <tr>
                                                <th>
                                                    {t('pages.documents.batchDropout.selectBatch')}
                                                </th>
                                                <th>
                                                    {t('pages.documents.batchDropout.batchNumber')}
                                                </th>
                                                <th>
                                                    {t(
                                                        'pages.documents.batchDropout.productionDate',
                                                    )}
                                                </th>
                                                <th>
                                                    {t(
                                                        'pages.documents.batchDropout.expirationDate',
                                                    )}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>{batchRows}</tbody>
                                    </BatchTable>
                                </BatchTableWrapper>
                            </BatchSection>
                        )}

                        <NavigationButtonsContainer>
                            <Button
                                size="sm"
                                style={{width: 'auto'}}
                                variant={ButtonVariant.Outline}
                                type="button"
                                disabled={isSearchPending || isRecallPending}
                                onClick={() => history.push(ROUTE.REGISTRIES_GTIN)}
                            >
                                {t('pages.products.recallForm.cancel')}
                            </Button>
                            <Button
                                size="sm"
                                style={{width: 'auto'}}
                                variant={ButtonVariant.Normal}
                                type="submit"
                                isLoading={isSearchPending}
                                disabled={
                                    !formState.isValid ||
                                    isRecallPending ||
                                    (batches.length > 1 && !selectedBatch)
                                }
                            >
                                {t('pages.products.recallForm.recall')}
                            </Button>
                        </NavigationButtonsContainer>
                    </FormWrapper>
                </form>
            </ContentWrapper>

            <Modal
                modalWidth={0}
                show={isConfirmationOpen}
                onClose={() => setIsConfirmationOpen(false)}
                title={t('pages.documents.batchDropout.confirmationTitle')}
            >
                <Root>
                    <ModalText>
                        {t('pages.documents.batchDropout.confirmationDescription', {
                            batchNumber: selectedBatch?.batchNumber,
                        })}
                    </ModalText>
                    <ButtonWrapper>
                        <Button
                            size="sm"
                            variant={ButtonVariant.Outline}
                            disabled={isRecallPending}
                            onClick={() => setIsConfirmationOpen(false)}
                        >
                            {t('pages.products.recallForm.recallConfirmationModal.cancel')}
                        </Button>
                        <Button
                            size="sm"
                            variant={ButtonVariant.Normal}
                            isLoading={isRecallPending}
                            onClick={confirmRecall}
                        >
                            {t('pages.products.recallForm.recallConfirmationModal.recall')}
                        </Button>
                    </ButtonWrapper>
                </Root>
            </Modal>
        </>
    );
};
